import { useMemo } from "react";

/**
 * Variant picker for a product detail page.
 *
 * Renders one row of pill buttons per attribute (Couleur, Taille, …).
 * Only variant combinations that exist + have stock > 0 are clickable.
 *
 * Props:
 *  - product: { variant_attributes: string[], variants: Variant[] }
 *  - selectedVariantId: string | null
 *  - onSelect: (variant) => void
 */
export default function VariantPicker({ product, selectedVariantId, onSelect }) {
  const attributes = product?.variant_attributes || [];
  const variants = product?.variants || [];
  const selectedVariant = useMemo(
    () => variants.find((v) => v.id === selectedVariantId) || null,
    [variants, selectedVariantId]
  );

  // For each attribute, derive its unique values in the same order they appear.
  const valuesByAttr = useMemo(() => {
    const acc = {};
    for (const attr of attributes) {
      const seen = new Set();
      const list = [];
      for (const v of variants) {
        const val = v.attributes?.[attr];
        if (val && !seen.has(val)) {
          seen.add(val);
          list.push(val);
        }
      }
      acc[attr] = list;
    }
    return acc;
  }, [attributes, variants]);

  if (variants.length === 0) return null;

  const handlePick = (attr, value) => {
    // Compose target attributes = currently selected + this change
    const next = { ...(selectedVariant?.attributes || {}), [attr]: value };
    // Find an exact match
    let match = variants.find((v) =>
      attributes.every((a) => v.attributes?.[a] === next[a])
    );
    // Fallback: match the chosen attr, ignore the rest (user is making first pick)
    if (!match) {
      match = variants.find((v) => v.attributes?.[attr] === value && v.stock > 0)
        || variants.find((v) => v.attributes?.[attr] === value);
    }
    if (match) onSelect(match);
  };

  return (
    <div className="mt-8 space-y-6" data-testid="variant-picker">
      {attributes.map((attr) => (
        <div key={attr}>
          <div className="flex items-baseline justify-between">
            <div className="text-xs font-bold uppercase tracking-[0.25em] text-[#C5CDD8]">
              {attr}
            </div>
            {selectedVariant?.attributes?.[attr] && (
              <div className="text-xs text-white/70" data-testid={`variant-current-${attr}`}>
                {selectedVariant.attributes[attr]}
              </div>
            )}
          </div>
          <div className="mt-3 flex flex-wrap gap-2">
            {(valuesByAttr[attr] || []).map((val) => {
              const variantForValue = variants.find(
                (v) => v.attributes?.[attr] === val
              );
              const isOut = !variantForValue || variantForValue.stock === 0;
              const isSelected = selectedVariant?.attributes?.[attr] === val;
              return (
                <button
                  key={val}
                  type="button"
                  onClick={() => !isOut && handlePick(attr, val)}
                  disabled={isOut}
                  className={`relative px-4 py-2.5 text-xs font-bold uppercase tracking-[0.15em] border transition-all min-w-[3.5rem] ${
                    isSelected
                      ? "bg-white text-[#001F3F] border-white"
                      : isOut
                      ? "bg-transparent text-white/30 border-white/15 cursor-not-allowed line-through"
                      : "bg-transparent text-white border-white/30 hover:border-white"
                  }`}
                  data-testid={`variant-option-${attr}-${val}`}
                  aria-pressed={isSelected}
                >
                  {val}
                  {variantForValue?.price_delta > 0 && !isOut && (
                    <span className="ml-1.5 text-[9px] text-[#FFD700] tracking-normal">
                      +{variantForValue.price_delta.toFixed(0)}&nbsp;€
                    </span>
                  )}
                </button>
              );
            })}
          </div>
        </div>
      ))}

      {selectedVariant && (
        <div
          className="text-xs text-white/55 -mt-2"
          data-testid="variant-stock-hint"
        >
          {selectedVariant.stock === 0
            ? "Variante en rupture"
            : selectedVariant.stock <= 8
            ? `⚠ Plus que ${selectedVariant.stock} en stock !`
            : `Stock : ${selectedVariant.stock}`}
        </div>
      )}
    </div>
  );
}
